import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Wifi,
  Server,
  Plus,
  MoreHorizontal,
  Edit2,
  Trash2,
  CheckCircle2,
  Lock,
  RefreshCw,
  AlertCircle,
  Key,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MQTTServerFormDialog } from '@/components/servers/MQTTServerFormDialog';
import { LNSServerFormDialog } from '@/components/servers/LNSServerFormDialog';
import { useToast } from '@/hooks/use-toast';
import { serversService } from '@/services';
import type { 
  ApiMQTTServer, 
  ApiLNSServer, 
  CreateMQTTServerRequest,
  UpdateMQTTServerRequest,
  CreateLNSServerRequest,
  UpdateLNSServerRequest,
} from '@/types/api';

// ============================================================================
// MQTT SERVER LIST
// ============================================================================

export function MQTTServerList() {
  const [formOpen, setFormOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<ApiMQTTServer | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serverToDelete, setServerToDelete] = useState<ApiMQTTServer | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query para obtener servidores MQTT
  // ACTUALIZADO: Backend ahora devuelve formato paginado { items: [], pagination: {} }
  const { data: servers = [], isLoading, error, refetch } = useQuery({
    queryKey: ['mqtt-servers'],
    queryFn: async () => {
      const response = await serversService.getMQTTServers();
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar servidores MQTT');
      }
      return response.data?.items || [];
    },
  });

  // Mutation para crear
  const createMutation = useMutation({
    mutationFn: (data: CreateMQTTServerRequest) => serversService.createMQTTServer(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['mqtt-servers'] });
        toast({
          title: 'Servidor MQTT creado',
          description: `${response.data?.name} ha sido añadido correctamente.`,
        });
        setFormOpen(false);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al crear servidor',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para actualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMQTTServerRequest }) =>
      serversService.updateMQTTServer(id, data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['mqtt-servers'] });
        toast({
          title: 'Servidor MQTT actualizado',
          description: `${response.data?.name} ha sido modificado.`,
        });
        setEditingServer(null);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al actualizar servidor',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para eliminar
  const deleteMutation = useMutation({
    mutationFn: (id: string) => serversService.deleteMQTTServer(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['mqtt-servers'] });
        toast({
          title: 'Servidor MQTT eliminado',
          description: 'El servidor ha sido eliminado.',
        });
        setDeleteDialogOpen(false);
        setServerToDelete(null);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al eliminar servidor',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleCreate = (data: CreateMQTTServerRequest | UpdateMQTTServerRequest) => {
    createMutation.mutate(data as CreateMQTTServerRequest);
  };

  const handleEdit = (data: CreateMQTTServerRequest | UpdateMQTTServerRequest) => {
    if (!editingServer) return;
    updateMutation.mutate({ id: editingServer.id, data: data as UpdateMQTTServerRequest });
  };

  const handleDelete = () => {
    if (!serverToDelete) return;
    deleteMutation.mutate(serverToDelete.id);
  };

  const testConnection = (server: ApiMQTTServer) => {
    toast({
      title: 'Probando conexión...',
      description: `Conectando a ${server.broker}:${server.port}`,
    });
    // TODO: Implementar test real cuando el backend lo soporte
    setTimeout(() => {
      toast({
        title: 'Conexión exitosa',
        description: `Conexión establecida con ${server.name}`,
      });
    }, 1500);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>{error instanceof Error ? error.message : 'Error al cargar servidores'}</span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reintentar
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {servers.length} servidor{servers.length !== 1 ? 'es' : ''} configurado{servers.length !== 1 ? 's' : ''}
          </p>
          <Button size="sm" onClick={() => setFormOpen(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Añadir Servidor
          </Button>
        </div>

        <AnimatePresence>
          {servers.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8 text-muted-foreground"
            >
              <Wifi className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay servidores MQTT configurados</p>
              <p className="text-sm">Añade uno para comenzar</p>
            </motion.div>
          ) : (
            <div className="space-y-3">
              {servers.map((server) => (
                <motion.div
                  key={server.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-background rounded-lg">
                      <Wifi className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{server.name}</h4>
                        {server.useTLS && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Lock className="w-3 h-3" />
                            TLS
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground font-mono">
                        {server.broker}:{server.port}
                        {server.username && <span className="text-xs ml-2">@{server.username}</span>}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => testConnection(server)}
                    >
                      Probar
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditingServer(server)}>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => {
                            setServerToDelete(server);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      <MQTTServerFormDialog
        open={formOpen || !!editingServer}
        onOpenChange={(open) => {
          if (!open) {
            setFormOpen(false);
            setEditingServer(null);
          }
        }}
        server={editingServer}
        onSubmit={editingServer ? handleEdit : handleCreate}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar servidor MQTT?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el servidor "{serverToDelete?.name}" 
              y los dispositivos que lo usen deberán ser reconfigurados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============================================================================
// LNS SERVER LIST
// ============================================================================

export function LNSServerList() {
  const [formOpen, setFormOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<ApiLNSServer | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serverToDelete, setServerToDelete] = useState<ApiLNSServer | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query para obtener servidores LNS
  // ACTUALIZADO: Backend ahora devuelve formato paginado { items: [], pagination: {} }
  const { data: servers = [], isLoading, error, refetch } = useQuery({
    queryKey: ['lns-servers'],
    queryFn: async () => {
      const response = await serversService.getLNSServers();
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar servidores LNS');
      }
      return response.data?.items || [];
    },
  });

  // Mutation para crear
  const createMutation = useMutation({
    mutationFn: (data: CreateLNSServerRequest) => serversService.createLNSServer(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['lns-servers'] });
        toast({
          title: 'Servidor LNS creado',
          description: `${response.data?.name} ha sido añadido correctamente.`,
        });
        setFormOpen(false);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al crear servidor',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para actualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLNSServerRequest }) =>
      serversService.updateLNSServer(id, data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['lns-servers'] });
        toast({
          title: 'Servidor LNS actualizado',
          description: `${response.data?.name} ha sido modificado.`,
        });
        setEditingServer(null);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al actualizar servidor',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para eliminar
  const deleteMutation = useMutation({
    mutationFn: (id: string) => serversService.deleteLNSServer(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['lns-servers'] });
        toast({
          title: 'Servidor LNS eliminado',
          description: 'El servidor ha sido eliminado.',
        });
        setDeleteDialogOpen(false);
        setServerToDelete(null);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al eliminar servidor',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const getLNSTypeName = (type: string) => {
    const types: Record<string, string> = {
      chirpstack_v3: 'ChirpStack V3',
      chirpstack_v4: 'ChirpStack V4',
    };
    return types[type] || type;
  };

  const handleCreate = (data: CreateLNSServerRequest | UpdateLNSServerRequest) => {
    createMutation.mutate(data as CreateLNSServerRequest);
  };

  const handleEdit = (data: CreateLNSServerRequest | UpdateLNSServerRequest) => {
    if (!editingServer) return;
    updateMutation.mutate({ id: editingServer.id, data: data as UpdateLNSServerRequest });
  };

  const handleDelete = () => {
    if (!serverToDelete) return;
    deleteMutation.mutate(serverToDelete.id);
  };

  const testConnection = (server: ApiLNSServer) => {
    toast({
      title: 'Probando conexión...',
      description: `Conectando a ${server.serverUrl}`,
    });
    // TODO: Implementar test real cuando el backend lo soporte
    setTimeout(() => {
      toast({
        title: 'Conexión exitosa',
        description: `Conexión establecida con ${server.name}`,
      });
    }, 1500);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>{error instanceof Error ? error.message : 'Error al cargar servidores'}</span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reintentar
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {servers.length} servidor{servers.length !== 1 ? 'es' : ''} configurado{servers.length !== 1 ? 's' : ''}
          </p>
          <Button size="sm" onClick={() => setFormOpen(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Añadir Servidor
          </Button>
        </div>

        <AnimatePresence>
          {servers.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8 text-muted-foreground"
            >
              <Server className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay servidores LNS configurados</p>
              <p className="text-sm">Añade uno para comenzar</p>
            </motion.div>
          ) : (
            <div className="space-y-3">
              {servers.map((server) => (
                <motion.div
                  key={server.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-background rounded-lg">
                      <Server className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium">{server.name}</h4>
                        <Badge variant="secondary" className="text-xs">
                          {getLNSTypeName(server.type)}
                        </Badge>
                        {server.hasApiKey && (
                          <Badge variant="outline" className="text-xs gap-1 text-success border-success/30">
                            <Key className="w-3 h-3" />
                            API Key
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground font-mono truncate max-w-[300px]">
                        {server.serverUrl}
                      </p>
                      {(server.controllerApplicationId || server.sensorApplicationId) && (
                        <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                          {server.controllerApplicationId && (
                            <span>Controllers: {server.controllerApplicationId.slice(0, 8)}...</span>
                          )}
                          {server.sensorApplicationId && (
                            <span>Sensors: {server.sensorApplicationId.slice(0, 8)}...</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => testConnection(server)}
                    >
                      Probar
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditingServer(server)}>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => {
                            setServerToDelete(server);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      <LNSServerFormDialog
        open={formOpen || !!editingServer}
        onOpenChange={(open) => {
          if (!open) {
            setFormOpen(false);
            setEditingServer(null);
          }
        }}
        server={editingServer}
        onSubmit={editingServer ? handleEdit : handleCreate}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar servidor LNS?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el servidor "{serverToDelete?.name}" 
              y los dispositivos que lo usen deberán ser reconfigurados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
